/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import { LicensePlate, SamlToken } from 'client-code/device-binding-service';
import { AuthConfiguration, IptvLoginResponse } from 'client-code/iptv-login';

import { IAuthServices } from '../interfaces/IAuthServices';
import { Log } from '../logging/Log';
import { Storage } from '../storage';
import { DeviceBindingService } from './DeviceBindingService';
import { IptvLoginService } from './IptvLoginService';

export class AuthServices implements IAuthServices {
    private TAG: string = 'AuthServices';

    public async getAuthConfiguration(): Promise<AuthConfiguration> {
        try {
            return await new IptvLoginService().fetchAndGetAuthConfiguration();
        } catch (error) {
            Log.e(this.TAG, 'getAuthConfiguration', error);
            throw error;
        }
    }

    public async getNewLicensePlate(): Promise<LicensePlate> {
        try {
            const licensePlate: LicensePlate = await new DeviceBindingService().fetchLicensePlate(
                true,
            );
            return licensePlate;
        } catch (error) {
            Log.e(this.TAG, 'getNewLicensePlate', error);
            throw error;
        }
    }

    public async getSamlTokenByLicensePlateCode(code?: string): Promise<SamlToken> {
        try {
            const lpAuthCode: string = this.getLicensePlateAuthCode();
            const sLpCode: string = code || lpAuthCode;
            const allowServiceRequest: boolean = !!sLpCode;
            if (!allowServiceRequest) {
                throw new Error('SamlTokenRequestNotAllowed');
            }
            const samlToken: SamlToken = await new DeviceBindingService().fetchSamlToken(sLpCode);
            this.storeLicensePlateAuthCode(sLpCode);
            return samlToken;
        } catch (error) {
            Log.e(this.TAG, 'getSamlTokenByLicensePlateCode', error);
            throw error;
        }
    }

    public async getIptvLoginBySamlToken(samlToken: string): Promise<IptvLoginResponse> {
        try {
            return await new IptvLoginService().getIptvLoginBySamlToken(samlToken);
        } catch (error) {
            Log.e(this.TAG, 'getIptvLoginBySamlToken', error);
            throw error;
        }
    }

    public async removeDeviceBinding(): Promise<void> {
        try {
            return await new DeviceBindingService().removeDeviceBinding();
        } catch (error) {
            Log.e(this.TAG, 'AuthServices::removeDeviceBinding()', error);
            throw error;
        }
    }

    /**
     * The method return a string that represents the license plate code used for
     * authenticating the user.
     * @returns `string` - the license plate auth code.
     */
    // eslint-disable-next-line class-methods-use-this
    private getLicensePlateAuthCode(): string {
        const lpAuthCode = Storage.getInstance().getItem(Storage.Keys.LICENSE_PLATE_AUTH_CODE);
        return lpAuthCode || '';
    }

    /**
     * The method stores the `licensePlateCode` used for user authentication and device binding
     * for future validation.
     */
    // eslint-disable-next-line class-methods-use-this
    private storeLicensePlateAuthCode(code: string): void {
        Storage.getInstance().setItem(Storage.Keys.LICENSE_PLATE_AUTH_CODE, code);
    }
}
